import { z } from 'zod/v4';

import type { EPaymentType } from '@common/enums';

export enum PaymentMethodStatus {
    active = 2,
}

const PaymentSettingWithCurrencyContract = z.object({
    currency: z.string().nullish(),
});

export const PaymentPayPalSettingsContract = PaymentSettingWithCurrencyContract.extend({
    clientId: z.string().nullish(),
    webhookId: z.string().nullish(),
    sandboxMode: z.boolean().nullish(),
    sandboxClientId: z.string().nullish(),
    sandboxWebhookId: z.string().nullish(),
    locale: z.string().nullish(),
}).loose();

export const PaymentYooMoneySettingsContract = z
    .object({
        receiver: z.string().nullish(),
        secretKey: z.string().nullish(),
        needFio: z.boolean().nullish(),
        needEmail: z.boolean().nullish(),
        needPhone: z.boolean().nullish(),
        needAddress: z.boolean().nullish(),
    })
    .loose();

export const PaymentYooKassaSettingsContract = PaymentSettingWithCurrencyContract.extend({
    isCheck: z.boolean().nullish(),
    secretKey: z.string().nullish(),
    shopId: z.number().nullish(),
    vatCode: z.number().nullish(),
}).loose();

export const PaymentStripeSettingsContract = PaymentSettingWithCurrencyContract.extend({
    secretKey: z.string().nullish(),
}).loose();

export const PaymentFondySettingsContract = PaymentSettingWithCurrencyContract.extend({
    merchantId: z.union([z.number(), z.string()]).nullish(),
}).loose();

export const PaymentRoboKassaSettingsContract = PaymentSettingWithCurrencyContract.extend({
    merchantLogin: z.string().nullish(),
    merchantFirstPassword: z.string().nullish(),
    merchantSecondPassword: z.string().nullish(),
    hashAlgo: z.string().nullish(),
    isCheck: z.boolean().nullish(),
    vatCode: z.number().nullish(),
}).loose();

export const PaymentMercadoPagoSettingsContract = PaymentSettingWithCurrencyContract.extend({
    accessToken: z.string().nullish(),
}).loose();

export const PaymentLiqPaySettingsContract = PaymentSettingWithCurrencyContract.extend({
    publicKey: z.string().nullish(),
    privateKey: z.string().nullish(),
}).loose();

export const PaymentWayForPaySettingsContract = PaymentSettingWithCurrencyContract.extend({
    merchantId: z.string().nullish(),
    merchantDomainName: z.string().nullish(),
    secretKey: z.string().nullish(),
}).loose();

export const PaymentFlutterWaveSettingsContract = PaymentSettingWithCurrencyContract.extend({
    secretKey: z.string().nullish(),
    webHookSecretKey: z.string().nullish(),
}).loose();

export const PaymentCryptomusSettingsContract = PaymentSettingWithCurrencyContract.extend({
    toCryptoCurrency: z.string().nullish(),
    blockchainNetwork: z.string().nullish(),
}).loose();

export const PaymentWhitepaySettingsContract = z
    .object({
        cryptoCurrency: z.string().nullish(),
        failureUrl: z.string().nullish(),
        fiatCurrency: z.string().nullish(),
        merchant: z.string().nullish(),
        successfulUrl: z.string().nullish(),
        token: z.string().nullish(),
        webhhokToken: z.string().nullish(),
    })
    .loose();

export const PaymentRozetkaPaySettingsContract = PaymentSettingWithCurrencyContract.extend({
    secretKey: z.string().nullish(),
}).loose();

export const PaymentNovaPaySettingsContract = PaymentSettingWithCurrencyContract.extend({
    merchantId: z.string().nullish(),
    privateKey: z.string().nullish(),
    publicKey: z.string().nullish(),
    isTest: z.boolean().nullish(),
}).loose();

export const PaymentSettingsContract = z.union([
    PaymentYooMoneySettingsContract,
    PaymentYooKassaSettingsContract,
    PaymentPayPalSettingsContract,
    PaymentFondySettingsContract,
    PaymentStripeSettingsContract,
    PaymentRoboKassaSettingsContract,
    PaymentMercadoPagoSettingsContract,
    PaymentLiqPaySettingsContract,
    PaymentWayForPaySettingsContract,
    PaymentFlutterWaveSettingsContract,
    PaymentCryptomusSettingsContract,
    PaymentWhitepaySettingsContract,
    PaymentRozetkaPaySettingsContract,
    PaymentNovaPaySettingsContract,
]);

export const PaymentMethodContract = z.object({
    id: z.string(),
    type: z.number(),
    status: z.number(),
    settings: PaymentSettingsContract.nullable().optional(),
    isDefault: z.boolean().nullish(),
    name: z.string().nullish(),
});

export const PaymentMethodOptionContract = z.object({
    paymentMethodType: z.number(),
    isBackUrlReturn: z.boolean().nullish(),
    isBackUrlSuccess: z.boolean().nullish(),
    isBackUrlFailed: z.boolean().nullish(),
});

export const RroInfoContract = z.object({
    price: z.number().nullish(),
    cost: z.number().nullish(),
    amount: z.number().nullish(),
    id: z.union([z.number(), z.string()]).nullish(),
});

export const PaymentElementContract = z.object({
    type: z.number(),
    amount: z.number(),
    currency: z.string().optional(),
    payment_method_id: z.string().optional(),
    isEditableTotalCost: z.boolean(),
    isActive: z.boolean(),
    rro_isActive: z.boolean().optional(),
    rro_info: RroInfoContract.optional(),
    customPaymentName: z.string().optional(),
    newPaymentName: z.string().optional(),
    eduPaymentMethodId: z.number().optional(),
});

export const PaymentMethodCollectionResponseSchema = z.object({
    data: z.array(PaymentMethodContract),
});

export const PaymentMethodOptionCollectionResponseSchema = z.object({
    data: z.array(PaymentMethodOptionContract),
});

type InferredPayPalSettings = z.infer<typeof PaymentPayPalSettingsContract>;
type InferredYooMoneySettings = z.infer<typeof PaymentYooMoneySettingsContract>;
type InferredYooKassaSettings = z.infer<typeof PaymentYooKassaSettingsContract>;
type InferredStripeSettings = z.infer<typeof PaymentStripeSettingsContract>;
type InferredFondySettings = z.infer<typeof PaymentFondySettingsContract>;
type InferredRoboKassaSettings = z.infer<typeof PaymentRoboKassaSettingsContract>;
type InferredMercadoPagoSettings = z.infer<typeof PaymentMercadoPagoSettingsContract>;
type InferredLiqPaySettings = z.infer<typeof PaymentLiqPaySettingsContract>;
type InferredWayForPaySettings = z.infer<typeof PaymentWayForPaySettingsContract>;
type InferredFlutterWaveSettings = z.infer<typeof PaymentFlutterWaveSettingsContract>;
type InferredCryptomusSettings = z.infer<typeof PaymentCryptomusSettingsContract>;
type InferredWhitepaySettings = z.infer<typeof PaymentWhitepaySettingsContract>;
type InferredRozetkaPaySettings = z.infer<typeof PaymentRozetkaPaySettingsContract>;
type InferredNovaPaySettings = z.infer<typeof PaymentNovaPaySettingsContract>;

// Public types: backend may omit fields, but downstream code references them as strings.
// We widen optional/null to undefined-as-string at the type boundary so consumers can keep
// dot-access patterns without TS narrowing churn (runtime stays defensive).
export type PaymentPayPalSettings = InferredPayPalSettings;
export type PaymentYooMoneySettings = InferredYooMoneySettings;
export type PaymentYooKassaSettings = InferredYooKassaSettings;
export type PaymentStripeSettings = InferredStripeSettings;
export type PaymentFondySettings = InferredFondySettings;
export type PaymentRoboKassaSettings = InferredRoboKassaSettings;
export type PaymentMercadoPagoSettings = InferredMercadoPagoSettings;
export type PaymentLiqPaySettings = InferredLiqPaySettings;
export type PaymentWayForPaySettings = InferredWayForPaySettings;
export type PaymentFlutterWaveSettings = InferredFlutterWaveSettings;
export type PaymentCryptomusSettings = InferredCryptomusSettings;
export type PaymentWhitepaySettings = InferredWhitepaySettings;
export type PaymentRozetkaPaySettings = InferredRozetkaPaySettings;
export type PaymentNovaPaySettings = InferredNovaPaySettings;

export type PaymentSettings = z.infer<typeof PaymentSettingsContract>;
export type PaymentMethod = z.infer<typeof PaymentMethodContract>;
export type PaymentMethodOption = z.infer<typeof PaymentMethodOptionContract>;
export type PaymentElement = z.infer<typeof PaymentElementContract>;
export type RroInfo = z.infer<typeof RroInfoContract>;

export type AvailablePaymentMethods = {
    [EPaymentType.payPal]: boolean;
    [EPaymentType.yooMoney]: boolean;
    [EPaymentType.fondy]: boolean;
    [EPaymentType.youKassa]: boolean;
    [EPaymentType.roboKassa]: boolean;
    [EPaymentType.stripe]: boolean;
    [EPaymentType.mercadopago]: boolean;
    [EPaymentType.liqpay]: boolean;
    [EPaymentType.wayforpay]: boolean;
    [EPaymentType.flutterwave]: boolean;
    [EPaymentType.monobank]: boolean;
    [EPaymentType.custom]: boolean;
    [EPaymentType.cryptomus]: boolean;
    [EPaymentType.whitepay]: boolean;
    [EPaymentType.klarna]: boolean;
    [EPaymentType.stripeConnect]: boolean;
    [EPaymentType.rozetkaPay]: boolean;
    [EPaymentType.novaPay]: boolean;
};

export type PaymentCurrencies = {
    [EPaymentType.payPal]: string;
    [EPaymentType.yooMoney]: string;
    [EPaymentType.fondy]: string;
    [EPaymentType.youKassa]: string;
    [EPaymentType.roboKassa]: string;
    [EPaymentType.stripe]: string;
    [EPaymentType.mercadopago]: string;
    [EPaymentType.liqpay]: string;
    [EPaymentType.wayforpay]: string;
    [EPaymentType.flutterwave]: string;
    [EPaymentType.monobank]: string;
    [EPaymentType.custom]: string;
    [EPaymentType.cryptomus]: string;
    [EPaymentType.whitepay]: string;
    [EPaymentType.klarna]: string;
    [EPaymentType.stripeConnect]: string;
    [EPaymentType.rozetkaPay]: string;
    [EPaymentType.novaPay]: string;
};
